import React, { useContext } from 'react'
import { Box } from '@mui/material'

import LanguageContext from "../../../contexts/LanguageContext"
import UserContext from "../../../contexts/UserContext"


import { TopBar, sansBlancsAutour } from "./FormatsHelpers"

import EditableField from "./EditableField"
import EditableButton from "./EditableButton"

/* FORMAT TITRE — EXCEPTION DE COMPOSITION (redesign Valentin 2026, decision
   Hicham 16/09/2026).
   ⛔ 01/10/2026 : le node cite, `8548-149399`, n'existe que dans le fichier
   d'ARCHIVE. La planche du fichier « Claude » est `3:141275`
   (section « 12 - Format Titre », Desktop, 1512 x 982).
   Contrairement aux autres formats, Figma pose FormatTitre comme un ecran a
   part : contenu CENTRE VERTICALEMENT, PAS de tag, PAS de footer, gros espace
   autour, titre mis en avant + sous-consigne + CTA « Demarrer » centre.
   -> On n'utilise donc NI CenterContainer (qui ancre volontairement le contenu
      en HAUT pour la stabilite de position des formats de sequence, correctif
      Nawal POINT 6) NI BottomBar. Cette exception ne vaut QUE pour FormatTitre :
      les autres formats gardent CenterContainer/BottomBar tels quels.
   ⚠️ Centrage par `margin: auto 0` et NON `justifyContent: center` : dans un
      conteneur qui peut defiler, le centrage flex rogne le HAUT du contenu des
      qu'il deborde, alors que les marges automatiques se resorbent d'elles-memes
      (meme parti pris qu'ailleurs dans le depot). Le centrage reste ainsi
      deterministe et robuste au contenu variable (titre long, sous-consigne
      variable) : si le bloc devient plus haut que la fenetre, il defile au lieu
      d'etre coupe, plutot qu'une hauteur figee. */
const FormatTitre = ({ actFormation, actIndex, format, setViewPlanCours, viewPlanCours, onlyContent, isEditor }) => {

  const { actLanguage } = useContext(LanguageContext)

  const { manageFormatResponse, manageSlide } = useContext(UserContext)

  return (
    <Box sx={{
      display:"flex",
      flexDirection:"column",
      /* ⚠️ `100vh` vaut la hauteur de la FENETRE, jamais celle d'un conteneur.
         Un apercu qui simule 982 px dans un panneau aurait donc affiche un ecran
         calibre sur la vraie fenetre. La variable `--hs-hauteur-ecran` n'est
         definie QUE par le panneau d'apercu ; partout ailleurs le repli `100vh`
         s'applique et rien ne change. Une variable CSS plutot qu'un prop : neuf
         signatures de composants restent intactes, et un format rendu hors du
         lecteur se comporte correctement sans qu'on ait pense a le lui dire. */
      height:onlyContent?"100%":"var(--hs-hauteur-ecran, 100vh)",
      /* ⛔ Le format devient son propre referentiel de mesure (cqw/cqh) : dans le
         lecteur il occupe la fenetre, dans l'apercu admin la boite 1500x1000. */
      containerType:"size",
      overflow:"hidden",
    }}>
      <TopBar
        actFormation={actFormation}
        actIndex={actIndex}
        setViewPlanCours={setViewPlanCours}
        viewPlanCours={viewPlanCours}
        onlyContent={onlyContent}
      />

      {/* Zone de contenu centree verticalement, propre a FormatTitre. */}
      <Box sx={{
        flex:1,
        minHeight:0,
        overflowY:onlyContent?"hidden":"auto",
        overflowX:"hidden",
        display:"flex",
        flexDirection:"column",
        /* ⛔ 04/10/2026 — LA ZONE DE LA PLANCHE (3:161294) : 40 px en haut,
           100 en bas, 40 sur les cotes (Mobile 3:161340 : 40, 80 et 20), contenu
           centre dans ce qui reste. Le code posait 24 a 48 px partout : le
           bloc tombait 60 px trop bas a 1512 x 982. md suit le Desktop. */
        padding:["40px 20px 80px", "40px 40px 100px", "40px 40px 100px", "40px 40px 100px"],
      }}>
        <Box sx={{
          margin:"auto 0",           // centrage vertical deterministe, sans rognage du haut
          width:"100%",
          display:"flex",
          flexDirection:"column",
          alignItems:"center",
          textAlign:"center",
          /* Entre le bloc texte et le bouton : 80 px Desktop (3:161294), 60
             Tablet (3:161326), 40 Mobile (3:161340). */
          gap:["40px", "60px", "80px", "80px"],
        }}>
          <Box sx={{
            width:"100%",
            display:"flex",
            flexDirection:"column",
            alignItems:"center",
            /* Titre / sous-titre : 40 px (3:161296, 3:161328), 20 Mobile (3:161342). */
            gap:["20px", "40px", "40px", "40px"],
          }}>
            <EditableField
              field={sansBlancsAutour(format?.title)}
              type="title"
              isEditor={isEditor}
              sx={{
                color:"text.black",
                /* ⚠️ ECHELLE DU TITRE RELUE DANS FIGMA (21/09/2026, lot 3a).
                   Jeton nomme « Text/Desktop/Heading/H1 » : Questrial Regular,
                   42 px, interligne 1,15, letterSpacing 0. Noeuds 3:302257
                   (Desktop), 3:282076 (Tablet), 3:282135 (Mobile, 26 px / 1,25).
                   Le 46 px du palier lg etait estime : la planche s'arrete a 42.
                   L'interligne n'etait declare NULLE PART, donc le navigateur
                   retombait sur `normal`, soit environ 1,15 a 1,2 selon la
                   police. On le pose explicitement. */
                /* ⛔ 04/10/2026 — LA PLANCHE TITRE A SON PROPRE CORPS : 46 px
                   (3:161297, 3:161329), 32 en Mobile (3:161343), interligne
                   1,15. Les 42 / 26 ci-dessus sont ceux des enonces de question. */
                fontSize:["32px", "46px", "46px", "46px"],
                lineHeight:"1.15",
                /* Rogne aux capitales et a la ligne de base, comme la planche :
                   ses ecarts de 40 / 80 px se comptent entre textes rognes
                   (3:161296, 3:161294). */
                textBox:"trim-both cap alphabetic",
                textAlign:"center",
                maxWidth:"1000px",
              }}
            />
            <EditableField
              field={format?.subTitle}
              type="subTitle"
              isEditor={isEditor}
              sx={{
                color:"text.subTitle",
                /* ⚠️ CONSIGNE RELUE DANS FIGMA (21/09/2026, lot 3a). Jeton
                   « Text/Desktop/Body/Large » : 20 px, interligne 1,5, aux deux
                   grands paliers ; 15 px en Mobile (3:282136). Cinq formats
                   portaient cinq echelles differentes pour le MEME element. */
                  fontSize:["15px", "20px", "20px", "20px"],
                  lineHeight:"1.5",
                textBox:"trim-both cap alphabetic",
                textAlign:"center",
                maxWidth:"650px",
              }}
            />
          </Box>
          {/* CTA « Demarrer » centre dans le contenu (remplace le CTA du footer,
              absent de la composition Figma).
              Libelle « Demarrer » (FR) / « Start » (EN) : Figma le specifie pour
              CET ecran de titre. Regle LOCALE a FormatTitre uniquement — on ne
              touche pas `languageValues.next` (partage par tous les autres
              formats). La logique de progression (isDone + manageSlide) est
              preservee a l'identique. */}
          <EditableButton
            isEditor={false}
            type="button"
            field={actLanguage === "en" ? "Start" : "Démarrer"}
            grand
            sx={{}}
            onClick={() => {
              manageFormatResponse(format, [{ key: 'isDone', value: true }])
              manageSlide(1)
            }}
          />
        </Box>
      </Box>
    </Box>
  )
}


export default FormatTitre
